import { useState, useEffect, CSSProperties } from 'react';
import { isClientSide } from '../../lib/isClientSide';
import { useWindowSize } from '../useWindowSize';
import { getScaleToHeightMap } from './getScaleToHeightMap';
import { getNodeInnerHeight } from './getNodeInnerHeight';

const getScale = (node: HTMLElement | null, container: HTMLElement | null): number | undefined => {
  if (!isClientSide) return undefined;

  if (!node || !container) return undefined;

  const heightScales = getScaleToHeightMap(node);

  const containerHeight = getNodeInnerHeight(container) || 1;

  const largestFit =
    heightScales.find(scale => scale.height <= containerHeight) ||
    heightScales[heightScales.length - 1];

  return largestFit.scale;
};

export const useAutoScale = ({
  container,
  node
}: {
  container: HTMLElement | null;
  node: HTMLElement | null;
}): CSSProperties => {
  const [scale, setScale] = useState<number | undefined>(undefined);
  const [size, setSize] = useState({
    width: 0,
    measure: '%'
  });

  const windowSize = useWindowSize();

  useEffect(() => setScale(container && node ? getScale(node, container) : 0), [
    windowSize.width,
    setScale,
    container,
    node
  ]);

  useEffect(() => {
    if (node) {
      const [width, measure] = node?.style.width.match(/[\d\\.]+|\D+/g) || [];

      if (width && measure) {
        setSize({
          width: Number(width),
          measure
        });
      }
    }
  }, [node]);

  if (scale === undefined) return {};

  return scale === 0
    ? { visibility: 'hidden' }
    : {
        transform: `scale(${scale})`,
        width: `${size.width / scale}${size.measure}`
      };
};
